import { Helmet } from "react-helmet-async";

interface SEOProps {
  title: string;
  description: string;
  keywords?: string[];
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  noIndex?: boolean;
  structuredData?: Record<string, unknown> | Record<string, unknown>[];
  publishDate?: string;
  modifiedDate?: string;
  author?: string;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords = [],
  canonicalUrl,
  ogImage = "https://www.dryoussefgam.com/logo-black.png",
  ogType = "website",
  noIndex = false,
  structuredData,
  publishDate,
  modifiedDate,
  author,
}) => {
  const siteUrl = "https://www.dryoussefgam.com";
  const fullCanonicalUrl = canonicalUrl || siteUrl;

  const structuredDataArray = structuredData
    ? Array.isArray(structuredData)
      ? structuredData
      : [structuredData]
    : [];

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      {keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(", ")} />
      )}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
      )}

      {/* Language and Geo Targeting */}
      <meta name="language" content="French" />
      <meta name="geo.region" content="TN-11" />
      <meta name="geo.placename" content="Tunis, Tunisie" />
      <link rel="alternate" hrefLang="fr" href={fullCanonicalUrl} />
      <link rel="alternate" hrefLang="fr-TN" href={fullCanonicalUrl} />
      <link rel="alternate" hrefLang="fr-FR" href={fullCanonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={fullCanonicalUrl} />

      {/* Canonical URL */}
      <link rel="canonical" href={fullCanonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:locale" content="fr_FR" />
      <meta property="og:locale:alternate" content="fr_TN" />
      <meta
        property="og:site_name"
        content="Dr Youssef Gam - Chirurgien Esthétique"
      />

      {/* Article Meta (for blog posts) */}
      {ogType === "article" && publishDate && (
        <meta property="article:published_time" content={publishDate} />
      )}
      {ogType === "article" && modifiedDate && (
        <meta property="article:modified_time" content={modifiedDate} />
      )}
      {ogType === "article" && author && (
        <meta property="article:author" content={author} />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullCanonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={title} />

      {/* Structured Data */}
      {structuredDataArray.map((data, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
